import { lazy, Suspense } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./components/Layout.js";
import { SessionGate } from "./components/SessionGate.js";
import { Carregando } from "./components/ui/index.js";
import { lerUsuarioAdmin } from "./lib/api.js";

// Páginas carregadas sob demanda (code-splitting por rota). Cada página vira
// seu próprio chunk — libs pesadas exclusivas viajam junto: jspdf só no chunk
// de Forense, leaflet só no de Geofeed. Antes tudo caía num bundle único de
// ~1,5 MB. Named exports → normalizamos pra { default } que o lazy() espera.
// T fica sem constraint de "todo export é componente" (alguns módulos exportam
// também helpers com props obrigatórias, ex.: ClienteDetalhe → DetalheClienteInline);
// `nome: keyof T` ainda protege contra typo, e o cast localiza o "é componente"
// no export de fato escolhido.
const lazyPagina = <T extends object, K extends keyof T>(
  loader: () => Promise<T>,
  nome: K,
) =>
  lazy(() =>
    loader().then((m) => ({ default: m[nome] as React.ComponentType<object> })),
  );

const PaginaDashboard = lazyPagina(() => import("./pages/Dashboard.js"), "PaginaDashboard");
const PaginaAlertas = lazyPagina(() => import("./pages/Alertas.js"), "PaginaAlertas");
const PaginaCartasPendentes = lazyPagina(() => import("./pages/CartasPendentes.js"), "PaginaCartasPendentes");
const PaginaClientes = lazyPagina(() => import("./pages/Clientes.js"), "PaginaClientes");
const PaginaCircuitos = lazyPagina(() => import("./pages/Circuitos.js"), "PaginaCircuitos");
const PaginaTopOfensores = lazyPagina(() => import("./pages/TopOfensores.js"), "PaginaTopOfensores");
const PaginaIoc = lazyPagina(() => import("./pages/Ioc.js"), "PaginaIoc");
const PaginaDdosInbound = lazyPagina(() => import("./pages/DdosInbound.js"), "PaginaDdosInbound");
const PaginaVinculos = lazyPagina(() => import("./pages/VinculosPendentes.js"), "PaginaVinculos");
const PaginaClienteDetalhe = lazyPagina(() => import("./pages/ClienteDetalhe.js"), "PaginaClienteDetalhe");
const PaginaEmails = lazyPagina(() => import("./pages/Emails.js"), "PaginaEmails");
const PaginaConfiguracoes = lazyPagina(() => import("./pages/Configuracoes.js"), "PaginaConfiguracoes");
const PaginaVerificacoes = lazyPagina(() => import("./pages/Verificacoes.js"), "PaginaVerificacoes");
const PaginaHistoricoIp = lazyPagina(() => import("./pages/HistoricoIp.js"), "PaginaHistoricoIp");
const PaginaProbeHistorico = lazyPagina(() => import("./pages/ProbeHistorico.js"), "PaginaProbeHistorico");
const PaginaTrocasPrefixo = lazyPagina(() => import("./pages/TrocasPrefixo.js"), "PaginaTrocasPrefixo");
const PaginaForense = lazyPagina(() => import("./pages/Forense.js"), "PaginaForense");
const PaginaDns = lazyPagina(() => import("./pages/Dns.js"), "PaginaDns");
const PaginaGeofeed = lazyPagina(() => import("./pages/Geofeed.js"), "PaginaGeofeed");
const PaginaInfraAuditoria = lazyPagina(() => import("./pages/InfraAuditoria.js"), "PaginaInfraAuditoria");
const PaginaInfraCatalogo = lazyPagina(() => import("./pages/InfraCatalogo.js"), "PaginaInfraCatalogo");
const PaginaInfraCircuitosDown = lazyPagina(() => import("./pages/InfraCircuitosDown.js"), "PaginaInfraCircuitosDown");
const PaginaManutencoes = lazyPagina(() => import("./pages/Manutencoes.js"), "PaginaManutencoes");
const PaginaInfraDashboard = lazyPagina(() => import("./pages/InfraDashboard.js"), "PaginaInfraDashboard");
const PaginaInfraDatacom = lazyPagina(() => import("./pages/InfraDatacom.js"), "PaginaInfraDatacom");
const PaginaInfraDevices = lazyPagina(() => import("./pages/InfraDevices.js"), "PaginaInfraDevices");
const PaginaInfraFirewall = lazyPagina(() => import("./pages/InfraFirewall.js"), "PaginaInfraFirewall");
const PaginaInfraHuawei = lazyPagina(() => import("./pages/InfraHuawei.js"), "PaginaInfraHuawei");
const PaginaInfraLogs = lazyPagina(() => import("./pages/InfraLogs.js"), "PaginaInfraLogs");
const PaginaInfraOrigensDesconhecidas = lazyPagina(() => import("./pages/InfraOrigensDesconhecidas.js"), "PaginaInfraOrigensDesconhecidas");
const PaginaInfraRoteadores = lazyPagina(() => import("./pages/InfraRoteadores.js"), "PaginaInfraRoteadores");
const PaginaInfraSessoes = lazyPagina(() => import("./pages/InfraSessoes.js"), "PaginaInfraSessoes");
const PaginaOficios = lazyPagina(() => import("./pages/Oficios.js"), "PaginaOficios");
const PaginaUsuarios = lazyPagina(() => import("./pages/Usuarios.js"), "PaginaUsuarios");
const PaginaAuditoria = lazyPagina(() => import("./pages/Auditoria.js"), "PaginaAuditoria");
const PaginaChangelog = lazyPagina(() => import("./pages/Changelog.js"), "PaginaChangelog");
const PaginaManual = lazyPagina(() => import("./pages/Manual.js"), "PaginaManual");
const PaginaAcessoCliente = lazyPagina(() => import("./pages/publico/AcessoCliente.js"), "PaginaAcessoCliente");
const PaginaMinhasNotificacoes = lazyPagina(() => import("./pages/publico/MinhasNotificacoes.js"), "PaginaMinhasNotificacoes");
const PaginaDetalheNotificacao = lazyPagina(() => import("./pages/publico/DetalheNotificacao.js"), "PaginaDetalheNotificacao");

/**
 * Página de entrada por papel.
 *
 * O Financeiro entra no sistema só para bloquear e desbloquear cliente — a
 * única tela a que tem acesso é Circuitos. Cair no Dashboard, que ele não pode
 * ver, é começar a sessão por uma parede: nada carrega, e quem não é técnico
 * conclui que o sistema está com problema.
 *
 * O menu lateral já esconde dele o que não pode abrir (ver Layout). Isto é a
 * mesma decisão aplicada à porta de entrada — e vale para `/admin` e para
 * qualquer rota desconhecida, que o roteador manda para cá.
 *
 * Só apresentação: quem barra de verdade continua sendo a permissão em cada
 * rota da API.
 */
function PaginaInicial() {
  const role = lerUsuarioAdmin()?.role;
  if (role === "financeiro") return <Navigate to="/admin/circuitos" replace />;
  return <PaginaDashboard />;
}

function PaginaNaoEncontrada() {
  return (
    <div className="casca-aviso-tela">
      <h1 className="casca-aviso-titulo">Não encontramos esta tela</h1>
      <p>O endereço não existe ou mudou de lugar. Use o menu ou abra o início.</p>
      <p>
        <Link to="/admin" className="casca-acao">Ir para o início</Link>
      </p>
    </div>
  );
}

export function App() {
  return (
    // Suspense de topo cobre as páginas públicas (sem Layout). As páginas
    // admin têm um Suspense próprio ao redor do <Outlet> no Layout, pra o
    // shell (sidebar/topbar) ficar visível durante o carregamento.
    <Suspense fallback={<TelaCarregando />}>
      <Routes>
        {/* Público — sem CF Access, autenticação via OTP do cliente */}
        <Route path="/c/:cartaId" element={<PaginaAcessoCliente />} />
        {/* `/cliente` é a URL canônica do portal — divulgar essa.
            `/cliente/login` mantido como alias pra compatibilidade. */}
        <Route path="/cliente" element={<PaginaAcessoCliente />} />
        <Route path="/cliente/login" element={<PaginaAcessoCliente />} />
        <Route path="/cliente/notificacoes" element={<PaginaMinhasNotificacoes />} />
        <Route path="/cliente/notificacoes/:id" element={<PaginaDetalheNotificacao />} />

        {/* Admin — protegido por CF Access + JWT interno */}
        <Route
          path="/admin"
          element={
            <SessionGate>
              <Layout />
            </SessionGate>
          }
        >
          <Route index element={<PaginaInicial />} />
          <Route path="alertas" element={<PaginaAlertas />} />
          <Route path="emails" element={<PaginaEmails />} />
          <Route path="cartas/pendentes" element={<PaginaCartasPendentes />} />
          <Route path="vinculos" element={<PaginaVinculos />} />
          <Route path="verificacoes" element={<PaginaVerificacoes />} />
          <Route path="historico-ip" element={<PaginaHistoricoIp />} />
          <Route path="probe-historico" element={<PaginaProbeHistorico />} />
          <Route path="circuitos" element={<PaginaCircuitos />} />
          <Route path="clientes" element={<PaginaClientes />} />
          <Route path="clientes/:id" element={<PaginaClienteDetalhe />} />
          <Route path="top-ofensores" element={<PaginaTopOfensores />} />
          <Route path="ioc" element={<PaginaIoc />} />
          <Route path="ddos-inbound" element={<PaginaDdosInbound />} />
          <Route path="trocas-prefixo" element={<PaginaTrocasPrefixo />} />
          <Route path="forense" element={<PaginaForense />} />
          <Route path="circuitos/down" element={<PaginaInfraCircuitosDown />} />
          <Route path="manutencoes" element={<PaginaManutencoes />} />
          <Route path="infra/dashboard" element={<PaginaInfraDashboard />} />
          <Route path="infra/devices" element={<PaginaInfraDevices />} />
          <Route path="infra/logs" element={<PaginaInfraLogs />} />
          <Route path="infra/firewall" element={<PaginaInfraFirewall />} />
          <Route path="infra/auditoria" element={<PaginaInfraAuditoria />} />
          <Route path="infra/datacom" element={<PaginaInfraDatacom />} />
          <Route path="infra/huawei" element={<PaginaInfraHuawei />} />
          <Route path="dns" element={<PaginaDns />} />
          <Route path="geofeed" element={<PaginaGeofeed />} />
          <Route path="infra/roteadores" element={<PaginaInfraRoteadores />} />
          <Route path="infra/sessoes" element={<PaginaInfraSessoes />} />
          <Route path="infra/origens-desconhecidas" element={<PaginaInfraOrigensDesconhecidas />} />
          <Route path="infra/catalogo" element={<PaginaInfraCatalogo />} />
          <Route path="oficios" element={<PaginaOficios />} />
          <Route path="usuarios" element={<PaginaUsuarios />} />
          <Route path="auditoria" element={<PaginaAuditoria />} />
          <Route path="manual" element={<PaginaManual />} />
          <Route path="changelog" element={<PaginaChangelog />} />
          <Route path="configuracoes" element={<PaginaConfiguracoes />} />
          {/* Endereço desconhecido DENTRO do painel: antes caía num redirecionamento mudo para o Dashboard
              (a pessoa não sabia que digitou/abriu algo que não existe). Agora diz, com o menu de pé. */}
          <Route path="*" element={<PaginaNaoEncontrada />} />
        </Route>

        <Route path="/" element={<Navigate to="/admin" replace />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Suspense>
  );
}

function TelaCarregando() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Carregando texto="Carregando…" />
    </div>
  );
}
